/**
 * The arithmetic behind an optimistic write: what a not-yet-filed expense
 * does to every balance already on the screen.
 *
 * **Why this can be exact rather than a guess.** Both balance reads are linear
 * in a transaction's edges, which is what makes predicting them honest:
 *
 *   * a group's balances are each person's net across `ledger_edges`, and the
 *     server rebuilds those as `pairwise(...)` of every transaction's own
 *     edges. `pairwise` nets a pair down; it does not change the signed sum
 *     for a pair, and a person's net is a sum of those sums. So the change to
 *     one person's group balance is exactly `netBalances` of the new lines.
 *   * `/api/balances` nets the same edges per (person, group). The change to
 *     your row against one person is exactly the signed sum of the new
 *     transaction's edges between the two of you.
 *
 * **And why it is the same arithmetic, not a second implementation of it.**
 * The lines are resolved here by `resolveSplit` and `linesFrom` from
 * `src/lib/money.ts` — literally the module `server/transactions.ts` imports.
 * Largest-remainder rounding exists once in this codebase, so the odd cent of
 * a three-way split lands on the same person in the prediction and in the
 * answer, and the reconciliation moves nothing. The one thing that must be
 * got right at the call site is the *order* participants are passed in, since
 * that is what breaks the tie: it has to be the group's own member order,
 * which is what `GET /api/groups/:id` returns its members in.
 *
 * Everything here is a pure function of a cached page and a draft. Nothing
 * touches the network, the query cache or React, which is what makes the
 * prediction testable on its own — and it is the part that would be
 * expensive to get wrong, because a wrong prediction is a number a person
 * reads and believes for as long as the request is in flight.
 */

import type {
  BalanceTotals,
  BalancesResponse,
  ExpenseDraft,
  GroupBalancesResponse,
  Person,
  Transaction,
  TransactionsResponse,
} from '../api'
import {
  edgesFor,
  linesFrom,
  netBalances,
  resolveSplit,
  type Edge,
  type Line,
  type UserId,
} from './money'

/**
 * The `transaction_lines` a draft will produce, resolved through the same
 * functions the server will resolve them through.
 *
 * `memberOrder` is the group's own order (`joined_at`, then `user_id`) and is
 * not optional: `splitCents` gives an odd cent to the earliest participant, so
 * passing them in the order a form's checkboxes happened to be ticked would
 * predict a cent onto the wrong person about a third of the time. Both the
 * payers and the participants are put in it here, exactly as
 * `server/transactions.ts` does before it divides anything — the server does
 * not take an order on trust from a client, and neither does the prediction.
 */
export function linesForDraft(draft: ExpenseDraft, memberOrder: readonly UserId[]): Line[] {
  const position = new Map(memberOrder.map((userId, index) => [userId, index]))
  const inOrder = <T extends { userId: UserId }>(entries: readonly T[]): T[] =>
    [...entries].sort(
      (a, b) =>
        (position.get(a.userId) ?? Number.MAX_SAFE_INTEGER) -
        (position.get(b.userId) ?? Number.MAX_SAFE_INTEGER),
    )

  const owed = resolveSplit(draft.amountCents, {
    mode: draft.split.mode,
    participants: inOrder(draft.split.participants),
  })

  return linesFrom(draft.amountCents, inOrder(draft.payers), owed)
}

/**
 * The row the expense list shows while the request is in flight. `pending` is
 * not a column and is never sent by the server; it is how the list knows to
 * draw the row as provisional, and it is gone the moment the real row
 * replaces it.
 */
export function draftTransaction(
  id: string,
  groupId: string,
  currency: string,
  draft: ExpenseDraft,
  lines: readonly Line[],
  now = new Date().toISOString().replace('T', ' ').slice(0, 19),
): Transaction {
  const on = (side: 'paid' | 'owes') =>
    lines
      .filter((line) => line.side === side)
      .map((line) => ({ userId: line.userId, amountCents: line.amountCents }))

  return {
    id,
    groupId,
    kind: draft.kind ?? 'expense',
    description: draft.description,
    category: draft.category,
    amountCents: draft.amountCents,
    currency,
    occurredOn: draft.occurredOn,
    note: null,
    splitMode: draft.split.mode,
    split: draft.split,
    createdBy: draft.payers[0]?.userId ?? '',
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    // Nothing this browser has just written can already have been confirmed:
    // a settlement starts unacknowledged, and an expense is nobody's to
    // acknowledge at all.
    confirmedAt: null,
    confirmedBy: null,
    projectId: draft.projectId ?? null,
    paid: on('paid'),
    owed: on('owes'),
    receiptCount: 0,
    pending: true,
  }
}

/** Puts a provisional row at the top of the list it belongs in. */
export function withDraftTransaction(
  page: TransactionsResponse | undefined,
  transaction: Transaction,
): TransactionsResponse | undefined {
  if (!page) return page
  return { ...page, transactions: [transaction, ...page.transactions] }
}

/** Drops a provisional row again — the request came back, or it failed. */
export function withoutTransaction(
  page: TransactionsResponse | undefined,
  id: string,
): TransactionsResponse | undefined {
  if (!page) return page
  return { ...page, transactions: page.transactions.filter((row) => row.id !== id) }
}

/**
 * How much each person's *group* balance moves. Positive means the group owes
 * them more than it did.
 */
export function groupDeltas(lines: readonly Line[]): Map<UserId, number> {
  return new Map(netBalances(lines).map((balance) => [balance.userId, balance.amountCents]))
}

/**
 * How much your row against each other person moves, from one transaction's
 * edges. Positive means they owe you more than they did.
 *
 * Edges that do not mention you change nothing on your home screen — they are
 * two other people's business — so they are skipped rather than netted.
 */
export function pairDeltas(myId: UserId, edges: readonly Edge[]): Map<UserId, number> {
  const deltas = new Map<UserId, number>()
  const add = (other: UserId, amountCents: number) =>
    deltas.set(other, (deltas.get(other) ?? 0) + amountCents)

  for (const edge of edges) {
    if (edge.debtorId === myId) add(edge.creditorId, -edge.amountCents)
    else if (edge.creditorId === myId) add(edge.debtorId, edge.amountCents)
  }
  return deltas
}

/** The same totals the server computes, from a complete list of people. */
export function totalsFor(people: readonly Person[]): BalanceTotals {
  let owedToYouCents = 0
  let youOweCents = 0
  let count = 0
  for (const person of people) {
    if (person.amountCents > 0) owedToYouCents += person.amountCents
    else if (person.amountCents < 0) youOweCents += -person.amountCents
    else continue
    count += 1
  }
  return {
    owedToYouCents,
    youOweCents,
    netCents: owedToYouCents - youOweCents,
    people: count,
  }
}

/**
 * The home screen's people list with a draft expense applied.
 *
 * A person the expense involves who is not on the list yet is added rather
 * than dropped — the first expense you ever share with somebody is exactly
 * the case where they are not there — in user-id order, which is the order
 * the endpoint itself pages in.
 *
 * The totals are recomputed from the whole list. That is right as long as the
 * list *is* the whole list, which is why the home screen asks for its people
 * in one page (`HOME_PEOPLE_LIMIT`); when there is a further page the totals
 * are left exactly as the server sent them, and the reconciliation that
 * follows the response corrects them.
 */
export function applyDraftToBalances(
  page: BalancesResponse | undefined,
  myId: UserId,
  group: { id: string; name: string },
  lines: readonly Line[],
  displayNames: ReadonlyMap<UserId, string>,
): BalancesResponse | undefined {
  if (!page) return page

  const deltas = pairDeltas(myId, edgesFor(lines))
  if (deltas.size === 0) return page

  const people: Person[] = []
  for (const person of page.people) {
    const delta = deltas.get(person.userId)
    if (delta === undefined || delta === 0) {
      people.push(person)
      continue
    }
    deltas.delete(person.userId)

    const groups = person.groups.some((share) => share.groupId === group.id)
      ? person.groups.map((share) =>
          share.groupId === group.id
            ? { ...share, amountCents: share.amountCents + delta }
            : share,
        )
      : [...person.groups, { groupId: group.id, groupName: group.name, amountCents: delta }]

    // A share that has netted to zero is a `ledger_edges` row that no longer
    // exists, so the server will not send it — and a person with no shares
    // left is a person the endpoint will not mention at all. Predicting them
    // as a row saying zero would be a row that vanishes a moment later, which
    // is exactly the flicker an optimistic screen is supposed to avoid.
    const live = groups.filter((share) => share.amountCents !== 0)
    if (live.length === 0) continue

    people.push({ ...person, amountCents: person.amountCents + delta, groups: live })
  }

  for (const [userId, delta] of deltas) {
    if (delta === 0) continue
    people.push({
      userId,
      displayName: displayNames.get(userId) ?? userId,
      amountCents: delta,
      groups: [{ groupId: group.id, groupName: group.name, amountCents: delta }],
    })
  }
  people.sort((a, b) => (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0))

  return {
    ...page,
    people,
    totals: page.nextCursor === null ? totalsFor(people) : page.totals,
  }
}

/** A group's balances with a draft expense applied, person by person. */
export function applyDraftToGroupBalances(
  page: GroupBalancesResponse | undefined,
  lines: readonly Line[],
): GroupBalancesResponse | undefined {
  if (!page) return page

  const deltas = groupDeltas(lines)
  if (deltas.size === 0) return page

  return {
    ...page,
    balances: page.balances.map((row) => {
      const delta = deltas.get(row.userId)
      return delta === undefined ? row : { ...row, amountCents: row.amountCents + delta }
    }),
  }
}

/**
 * The group's balances as the write's own response states them — the
 * reconciliation.
 *
 * `POST /api/groups/:id/transactions` answers with the whole ledger after the
 * write, derived from the edges rather than recounted, so this is the
 * authoritative number and not another prediction. Anybody the ledger no
 * longer mentions is settled up, which is a zero rather than a disappearance:
 * a row that vanished would read as "this person left", and they did not.
 */
export function reconcileGroupBalances(
  page: GroupBalancesResponse | undefined,
  balances: readonly { userId: UserId; amountCents: number }[],
): GroupBalancesResponse | undefined {
  if (!page) return page
  const settled = new Map(balances.map((balance) => [balance.userId, balance.amountCents]))
  return {
    ...page,
    balances: page.balances.map((row) => ({ ...row, amountCents: settled.get(row.userId) ?? 0 })),
  }
}
